"use client";

import { clsx } from "clsx";
import type { Key } from "react";
import { cva } from "class-variance-authority";
import { useTranslations } from "next-intl";
import { Button, Select as RACSelect, SelectValue } from "react-aria-components";

import type { TierId } from "@mw/shared/pricing";
import { ListBox } from "@ui/components/core/ListBox";
import IconChevronGrabberVertical from "@ui/central-icons/icons/IconChevronGrabberVertical";

import { CornerMarkerDot } from "../core/CornerMarkerDot";
import type { ColumnCount } from "./PricingTableV3Shared";
import { useFormatTierPrice } from "./PricingValueRenderer";
import type { FormattedValue } from "./pricing-value-types";

export type BillingPeriod = "monthly" | "yearly";

/** Pre-translated tier info passed from server */
export interface TranslatedTierInfo {
  id: TierId;
  title: string;
  monthlyPrice?: number;
  monthlyDiscountedPrice?: number;
  monthlyDiscountMessageKey?: string;
  yearlyPrice?: number;
  yearlyDiscountedPrice?: number;
  yearlyDiscountMessageKey?: string;
}

/** Pre-translated section data passed from server */
export interface TranslatedSection {
  id: string;
  /** Section title - used by ApiPricingTable, not used by AgentsPricingTable */
  title?: string;
  /** Section description - used by ApiPricingTable, not used by AgentsPricingTable */
  description?: string;
  /** CTA button label - used by ApiPricingTable, not used by AgentsPricingTable */
  ctaLabel?: string;
  /** CTA button href - used by ApiPricingTable, not used by AgentsPricingTable */
  ctaHref?: string;
  subSections: Array<{
    label?: string;
    rows: Array<{
      label: string;
      /** Optional tooltip text (pre-translated) */
      tooltip?: string;
      values: Array<{
        tierId: TierId;
        value: FormattedValue;
      }>;
    }>;
  }>;
}

// Responsive grid using fr units to fit columns without horizontal scrolling
export const pricingTableGrid = cva("tw-grid tw-grid-cols-2", {
  variants: {
    columnCount: {
      1: "md:tw-grid-cols-[1.5fr_1fr] lg:tw-grid-cols-[2fr_1fr]",
      2: "md:tw-grid-cols-[1.5fr_repeat(2,1fr)] lg:tw-grid-cols-[2fr_repeat(2,1fr)]",
      3: "md:tw-grid-cols-[1.5fr_repeat(3,1fr)] lg:tw-grid-cols-[2fr_repeat(3,1fr)]",
      4: "md:tw-grid-cols-[1.5fr_repeat(4,1fr)] lg:tw-grid-cols-[2fr_repeat(4,1fr)]",
      5: "md:tw-grid-cols-[1.5fr_repeat(5,1fr)] lg:tw-grid-cols-[2fr_repeat(5,1fr)]",
      6: "md:tw-grid-cols-[1.5fr_repeat(6,1fr)] lg:tw-grid-cols-[2fr_repeat(6,1fr)]",
      7: "md:tw-grid-cols-[1.5fr_repeat(7,1fr)] lg:tw-grid-cols-[2fr_repeat(7,1fr)]",
      8: "md:tw-grid-cols-[1.5fr_repeat(8,1fr)] lg:tw-grid-cols-[2fr_repeat(8,1fr)]",
    },
  },
  defaultVariants: {
    columnCount: 6,
  },
});

interface TierPriceInfoProps {
  tier: TranslatedTierInfo;
  className?: string;
  billingPeriod?: BillingPeriod;
}

/** Displays tier price info (used in both header and mobile selector) */
export const TierPriceInfo = ({ tier, className, billingPeriod = "monthly" }: TierPriceInfoProps) => {
  const formatPrice = useFormatTierPrice();
  const tPricingCard = useTranslations("PricingClientKeys.PricingCard");

  const isYearly = billingPeriod === "yearly";
  const basePrice = isYearly ? tier.yearlyPrice : tier.monthlyPrice;
  const discountedPrice = isYearly ? tier.yearlyDiscountedPrice : tier.monthlyDiscountedPrice;
  const discountMessageKey = isYearly ? tier.yearlyDiscountMessageKey : tier.monthlyDiscountMessageKey;

  if (tier.id === "payg") {
    return null;
  }
  if (basePrice === undefined) {
    return <div className={className}>{tPricingCard("custom_pricing")}</div>;
  }

  // For monthly with first_month discount, show base price + discount message
  // For yearly discounts (continuous), show the discounted price
  const hasFirstMonthDiscount = !isYearly && discountedPrice && discountMessageKey?.startsWith("first_month");
  const displayPrice = hasFirstMonthDiscount ? basePrice : (discountedPrice ?? basePrice);

  const getDiscountMessage = () => {
    if (!discountedPrice || !discountMessageKey) return null;
    if (!isYearly && discountMessageKey.startsWith("first_month")) {
      return tPricingCard("first_month", { price: formatPrice(discountedPrice) });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return tPricingCard(discountMessageKey as any);
  };

  const discountMessage = getDiscountMessage();

  return (
    <div className={className}>
      {formatPrice(displayPrice)} {tPricingCard("period_month_long")}
      {discountMessage && <>, {discountMessage}</>}
    </div>
  );
};

interface TierHeaderProps {
  tiers: TranslatedTierInfo[];
  billingPeriod: BillingPeriod;
}

/** Sticky tier header (desktop only) */
export const TierHeader = ({ tiers, billingPeriod }: TierHeaderProps) => {
  const columnCount = tiers.length as ColumnCount;

  return (
    <div className='tw-sticky tw-top-[54px] tw-z-30 tw-border-y max-md:tw-hidden tw-border-black/5 tw-bg-paper'>
      <CornerMarkerDot position='topLeft' />
      <CornerMarkerDot position='topRight' />

      <div className={clsx(pricingTableGrid({ columnCount }))}>
        <div />
        {tiers.map(tier => (
          <div
            key={tier.id}
            className='tw-py-4 tw-px-2 lg:tw-py-6 lg:tw-px-4 tw-text-right tw-border-l tw-relative tw-border-black/5'
          >
            <CornerMarkerDot position='topLeft' />
            <div className='tw-text-black tw-type-lg tw-mb-2'>{tier.title}</div>
            <TierPriceInfo tier={tier} billingPeriod={billingPeriod} className='tw-text-gray-600 tw-type-xs' />
          </div>
        ))}
      </div>
    </div>
  );
};

interface MobileTierSelectorProps {
  tiers: TranslatedTierInfo[];
  selectedTierId: TierId;
  onTierChange: (tierId: TierId) => void;
  billingPeriod: BillingPeriod;
}

/** Mobile tier selector */
export const MobileTierSelector = ({ tiers, selectedTierId, onTierChange, billingPeriod }: MobileTierSelectorProps) => {
  const selectedTier = tiers.find(t => t.id === selectedTierId) || tiers[0];

  return (
    <div className='tw-sticky tw-top-[54px] tw-z-30 tw-p-4 tw-border-b md:tw-hidden tw-border-black/5 tw-bg-paper'>
      <RACSelect
        selectedKey={selectedTierId}
        onSelectionChange={(key: Key | null) => {
          if (key !== null && typeof key === "string") {
            onTierChange(key as TierId);
          }
        }}
        aria-label='Select tier'
        className='tw-w-full'
      >
        <Button
          className={({ isFocusVisible }) =>
            clsx(
              "tw-w-full tw-text-black tw-type-lg tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-bg-transparent",
              isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
            )
          }
        >
          <SelectValue className='tw-flex-1 tw-text-left'>{selectedTier.title}</SelectValue>
          <IconChevronGrabberVertical className='tw-size-5 tw-text-black tw-flex-none' />
        </Button>
        <ListBox items={tiers.map(tier => ({ id: tier.id, textValue: tier.title }))} offset={8} />
      </RACSelect>
      <TierPriceInfo
        tier={selectedTier}
        billingPeriod={billingPeriod}
        className='tw-text-gray-600 tw-type-xs tw-mt-2'
      />
    </div>
  );
};
